import { ApplicationConfig, LOCALE_ID, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { HolcimTheme } from './shared/theme/holcim-theme';

import {
  SOCIAL_AUTH_CONFIG,
  SocialAuthService,
  GoogleLoginProvider,
  SocialAuthServiceConfig
} from '@abacritt/angularx-social-login';
import { BnNgIdleService } from 'bn-ng-idle';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './interceptors/auth.interceptor';
import { menuDataCacheInterceptor } from './interceptors/menu-data-cache.interceptor';
import { SpinnerService } from './services/spinner.service';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { AuthService } from './services/auth.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, menuDataCacheInterceptor])),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      if (authService.isLoggedIn()) {
        authService.fetchUserProfile(0, true).subscribe();
      }
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: HolcimTheme,
        options: {
          darkModeSelector: false
        }
      },
      zIndex: {
        modal: 2100,
        overlay: 2200,
        menu: 2200,
        tooltip: 2300
      }
    }),

    // SocialAuthService has providedIn: 'root', but we explicitly provide
    // the SOCIAL_AUTH_CONFIG token that it actually injects internally
    SocialAuthService,
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        lang: 'en',
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              environment.googleClientId,
              {
                oneTapEnabled: false,
                scopes: 'email profile openid'
              }
            )
          }
        ],
        onError: (err) => {
          console.error('Google Social Auth Error:', err);
          AuthService.broadcastLoginError(err);
        }
      } as SocialAuthServiceConfig
    },

    SpinnerService,
    DialogService,
    MessageService,
    // Inactivity detector used by AuthService to warn about long-idle sessions.
    // Provided explicitly for consistency; the library is already
    // providedIn: 'root'.
    BnNgIdleService,
    {
      provide: LOCALE_ID,
      useFactory: () => navigator.language || 'en-US'
    }
  ]
};